import { useApolloClient } from '@apollo/client';
import {
  Text,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Checkbox,
  HStack,
  Box,
  useColorModeValue,
  VStack,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { indexBy } from 'remeda';

import {
  typeguards as commonTypeguards,
  date as commonDate,
  finleyAssert,
  path as commonPath,
  orgProvidedInputs as commonOrgProvidedInputs,
} from '@finley-technologies/common';
import { OrgProvidedInputs, Util } from '@finley-technologies/finley-types';

import {
  OrgProvidedInputGQLType,
  OrgProvidedValueGQLType,
  useCreateOrgProvidedValueMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Select } from 'packages/common-frontend/components/Select';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { UploadScreenshotFormField } from 'packages/common-frontend/components/UploadScreenshotFormField';
import { GET_ORG_PROVIDED_VALUES_SUPPLEMENTAL_UPLOAD_SIGNED_URL } from 'packages/files/requests/files.gql';
import { presignedPostUpload } from 'packages/files/ui/utils';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';

// Types
type OrgProvidedValue = Omit<
  OrgProvidedValueGQLType,
  'id' | 'version' | 'value'
> & { value: string };

type OrgProvidedValueWithFile = OrgProvidedValue & { file?: File };

type ValueCommonProps = {
  formProps: FormikProps<OrgProvidedValue>;
};

// Fragments
const StringValue = ({ formProps }: ValueCommonProps) => {
  return (
    <Input
      value={formProps.values.value}
      onChange={(e) => formProps.setFieldValue('value', e.target.value)}
      type="text"
    />
  );
};

const NumberValue = ({ formProps }: ValueCommonProps) => {
  return (
    <Input
      value={formProps.values.value}
      onChange={(e) => {
        formProps.setFieldValue('value', e.target.value);
      }}
      type="number"
      formNoValidate
    />
  );
};

const BooleanValue = ({ formProps }: ValueCommonProps) => {
  return (
    <Checkbox
      checked={formProps.values.value === 'true'}
      onChange={(e) =>
        formProps.setFieldValue('value', e.target.checked ? 'true' : 'false')
      }
      type="number"
    />
  );
};

const FullDateStrValue = ({ formProps }: ValueCommonProps) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <SimpleDatePicker
      tz={clientTimeZone}
      dateFormat="MMMM dd, yyyy"
      selectedDate={
        commonTypeguards.isFullDateStr(formProps.values.value)
          ? commonDate.fullDateStrToDate(formProps.values.value)
          : undefined
      }
      setSelectedDate={(date: Date | null) => {
        if (!date) return;
        formProps.setFieldValue(
          'value',
          commonDate.dateToFullDateString(date, clientTimeZone),
        );
      }}
    />
  );
};

type ValueCreateFormProps = {
  orgProvidedInputs: OrgProvidedInputs.OrgProvidedInput[];
  reportingDate?: Util.FullDateStr;
  onCancel: Function;
  onSubmit: (orgProvidedValueId: string) => Promise<void>;
};
export const ValueCreateForm = ({
  orgProvidedInputs,
  reportingDate,
  onCancel,
  onSubmit,
}: ValueCreateFormProps) => {
  const client = useApolloClient();
  const toast = useToast();

  const [loading, setLoading] = useState(false);

  const [createOrgProvidedValue] = useCreateOrgProvidedValueMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'An unexpected error occurred. Please contact Finley for support.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedInput, setSelectedInput] = useState(orgProvidedInputs[0]);

  const keyMap = indexBy(orgProvidedInputs, (input) => input.key);

  const selectOptions: {
    value: string;
    label: string;
  }[] = orgProvidedInputs.map((input) => ({
    value: input.key,
    label: input.key,
  }));

  const buttonColorScheme = useColorModeValue('aurora', 'gray');

  const valueCreateModalOnSubmit = async ({
    orgProvidedInput,
    orgProvidedValue,
  }: {
    orgProvidedInput: OrgProvidedInputGQLType;
    orgProvidedValue: OrgProvidedValueWithFile;
  }) => {
    const {
      value: rawValue,
      orgProvidedInputId,
      reportingDate: orgProvidedValueReportingDate,
      file,
    } = orgProvidedValue;

    const serializedValue = commonOrgProvidedInputs.serializeValue(rawValue);

    const deserializedValue = commonOrgProvidedInputs.deserializeValue({
      type: orgProvidedInput.type,
      value: serializedValue,
      nullable: orgProvidedInput.nullable,
      orgProvidedInputId,
    });

    let supplementalS3Path: string | null = null;
    if (file) {
      const { s3FilePath } = await presignedPostUpload<
        { orgProvidedInputId: string; fileExtension: string },
        {}
      >({
        client,
        file,
        query: GET_ORG_PROVIDED_VALUES_SUPPLEMENTAL_UPLOAD_SIGNED_URL,
        queryVariables: {
          orgProvidedInputId,
          fileExtension: commonPath.getOutputReportFileExtension(file.name),
        },
      });
      supplementalS3Path = s3FilePath;
    }

    const { data: orgProvidedValueData } = await createOrgProvidedValue({
      variables: {
        orgProvidedInputId,
        value: deserializedValue,
        reportingDate: orgProvidedValueReportingDate,
        supplementalS3Path,
      },
    });

    finleyAssert(
      orgProvidedValueData,
      `createOrgProvidedValue returned ${orgProvidedValueData}`,
    );

    return orgProvidedValueData.createOrgProvidedValue.id;
  };

  return (
    <Formik<OrgProvidedValueWithFile>
      initialValues={{
        file: undefined,
        orgProvidedInputId: selectedInput.id,
        value: '',
        reportingDate: reportingDate ?? commonDate.currentDateStr(),
      }}
      onSubmit={async (props) => {
        try {
          setLoading(true);
          const orgProvidedValueId = await valueCreateModalOnSubmit({
            orgProvidedInput: selectedInput,
            orgProvidedValue: props,
          });
          await onSubmit(orgProvidedValueId);
          toast({
            status: 'success',
            position: 'top',
            duration: 5000,
            isClosable: true,
            title: 'Successfully created org provided value.',
          });
        } catch (err) {
          toast({
            status: 'error',
            position: 'top',
            duration: 5000,
            isClosable: true,
            title:
              commonTypeguards.isFinleyErrorType(err) &&
              err.externalMessage &&
              err.externalMessage.length > 0
                ? err.externalMessage
                : 'Unable to create value. Please see technical assistance.',
          });
        } finally {
          setLoading(false);
        }
      }}
    >
      {(formProps: FormikProps<OrgProvidedValueWithFile>) => (
        <Form onSubmit={formProps.handleSubmit}>
          <VStack spacing="20px">
            <Flex alignSelf="flex-start" width="full">
              <Text alignSelf="center" w="40%" fontWeight="medium">
                Key
              </Text>
              <Box width="full">
                <Select
                  value={selectedInput.key}
                  onChange={(key) => {
                    if (key) {
                      const input = keyMap[key];
                      finleyAssert(
                        input,
                        `OrgProvidedInput does not exist for key ${key}`,
                      );
                      setSelectedInput(input);
                      formProps.setFieldValue('orgProvidedInputId', input.id);
                      if (input.type === 'boolean') {
                        formProps.setFieldValue('value', 'false');
                      } else {
                        formProps.setFieldValue('value', '');
                      }
                    }
                  }}
                  options={selectOptions}
                  width="full"
                />
              </Box>
            </Flex>
            <Flex alignSelf="flex-start" width="full">
              <Text alignSelf="center" w="40%" fontWeight="medium">
                Value
              </Text>
              <Box width="full">
                {selectedInput.type === 'string' && (
                  <StringValue formProps={formProps} />
                )}
                {selectedInput.type === 'number' && (
                  <NumberValue formProps={formProps} />
                )}
                {selectedInput.type === 'boolean' && (
                  <BooleanValue formProps={formProps} />
                )}
                {selectedInput.type === 'fullDateStr' && (
                  <FullDateStrValue formProps={formProps} />
                )}
              </Box>
            </Flex>

            <SimpleDatePicker
              tz={clientTimeZone}
              dateFormat="MMMM dd, yyyy"
              selectedDate={commonDate.fullDateStrToDate(
                formProps.values.reportingDate,
              )}
              label="Reporting Date"
              width="full"
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                formProps.setFieldValue(
                  'reportingDate',
                  commonDate.dateToFullDateString(date, clientTimeZone),
                );
              }}
            />

            <Divider my={3} />
            <Text alignSelf="flex-start" fontWeight="medium">
              Screenshot (Optional)
            </Text>
            <UploadScreenshotFormField
              isDisabled={false}
              setFieldValue={(value: FileWithPath) =>
                formProps.setFieldValue('file', value)
              }
              accept={{
                'image/png': ['.png'],
              }}
            />

            <Box py="12px" alignSelf="flex-end">
              <HStack spacing="25px">
                <Button
                  variant="outline"
                  colorScheme={buttonColorScheme}
                  onClick={() => onCancel()}
                  px="20px"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  colorScheme={buttonColorScheme}
                  type="submit"
                  px="20px"
                  isLoading={loading}
                >
                  Create
                </Button>
              </HStack>
            </Box>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

type ValueCreateModalProps = {
  isOpen: boolean;
  onCancel: Function;
  onSubmit: (orgProvidedValueId: string) => Promise<void>;
  orgProvidedInputs: OrgProvidedInputs.OrgProvidedInput[];
  reportingDate?: Util.FullDateStr;
};
export const ValueCreateModal = ({
  isOpen,
  onCancel,
  onSubmit,
  orgProvidedInputs,
  reportingDate,
}: ValueCreateModalProps) => {
  finleyAssert(orgProvidedInputs.length, 'No keys are configured.');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>Create Org Provided Value</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <ValueCreateForm
            orgProvidedInputs={orgProvidedInputs}
            reportingDate={reportingDate}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
